import { z } from "zod";

/**
 * El billing del negocio: lo que debe, con qué plan y cómo se le cobra.
 *
 * # De dónde sale
 *
 * `GET /api/v1/business/billing` y `/bills` en el BFF, que **proxea** a octopus
 * (trd-bff#257 / trd-bff#263). Como es un proxy, las formas son las de octopus
 * y vienen en **snake_case** — por eso los campos se leen así y no en camelCase
 * como el resto del cliente. El contrato está en trd-octopus#294 y #295.
 *
 * # La plata viaja como STRING
 *
 * `amount`, `price` y `balance.amount` son **strings decimales** ("25000.00").
 * No los pases por `parseFloat` para calcular: `formatCurrency` los acepta tal
 * cual (usa currency.js, que parsea decimales sin perder centavos).
 *
 * # Los esquemas son permisivos a propósito
 *
 * Se validan los campos que la pantalla usa y se dejan pasar los demás
 * (`.passthrough()`). El día que octopus agregue un campo, la pantalla no se
 * cae: un `z.object()` estricto convierte una adición inofensiva del backend en
 * una pantalla en blanco.
 */

/** Un pago pendiente: lo que se debe por un ciclo de la suscripción. */
export const billSchema = z
  .object({
    id: z.string(),
    cycle: z.number().optional().nullable(),
    period_start: z.string().optional().nullable(),
    period_end: z.string().optional().nullable(),
    /** String decimal. Es plata: no parsear a float. */
    amount: z.string(),
    currency: z.string().optional().nullable(),
    /**
     * `open` · `paid` · `void` · `under_review`.
     *
     * **No es un `z.enum`, a propósito.** Un enum estricto acá no protege nada:
     * el día que octopus agrega un estado, zod tira, la query entera falla y la
     * pantalla muestra "Todavía no hay pagos" con facturas del otro lado. Ya
     * pasó — ver el docstring de `paid_via`.
     *
     * `under_review` es una transferencia reportada que todavía nadie revisó:
     * **no está abierta ni pagada**. Tratarla como pendiente invitaría a pagar
     * dos veces.
     */
    status: z.string(),
    due_at: z.string().optional().nullable(),
    /** `true` cuando ya pasó el vencimiento y sigue abierto. */
    overdue: z.boolean().optional().nullable(),
    paid_at: z.string().optional().nullable(),
    /**
     * Por dónde entró el pago. `null` si todavía no se pagó.
     *
     * `automatic` (el motor con la tarjeta guardada) · `checkout` (el dueño pagó
     * desde la app) · `transfer` (transferencia aprobada por staff) · `external`
     * (se asentó a mano un cobro que entró por fuera). `manual` es el nombre
     * VIEJO de `checkout`: octopus lo renombró porque chocaba con el *modo* de
     * cobro, y sigue aceptando el viejo al hidratar filas ya guardadas.
     *
     * **Era un `z.enum(["automatic", "manual"])` y eso rompió la pantalla**
     * (2026-09-17): con una factura paga cuyo `paid_via` era `"checkout"`, zod
     * rechazaba la respuesta ENTERA y el historial mostraba "Todavía no hay
     * pagos". Un valor nuevo del backend no puede vaciar una pantalla de plata.
     */
    paid_via: z.string().optional().nullable(),
    provider_ref: z.string().optional().nullable(),
  })
  .passthrough();

export type Bill = z.infer<typeof billSchema>;

export const billingSummarySchema = z
  .object({
    /**
     * `active` · `past_due` · `canceled` · `pending_first_payment` (una
     * recontratación hecha desde Mi Suscripción, esperando su primer pago;
     * octopus v1.52.0).
     *
     * Tampoco es un enum, y acá el alcance es peor: esto viaja en el RESUMEN,
     * así que un estado nuevo no rompería el historial sino la pantalla entera.
     */
    status: z.string(),
    /**
     * Billing le **revocó la capacidad** al negocio por mora: es lo que
     * proyecta a identity y lo que ve el PDP. `status: past_due` con
     * `suspended: false` es la mora que todavía opera.
     *
     * No es lo mismo que `dunning.step`: ver ahí.
     */
    suspended: z.boolean().optional().nullable(),
    /** Orientación comercial de Octopus; no es autorización ni ACK de Identity. */
    recovery: z
      .object({
        action: z.string(),
        reason: z.string().optional().nullable(),
        bill_id: z.string().optional().nullable(),
        /** El contrato vigente; el que devolvió la recontratación (octopus v1.52.0). */
        contract_id: z.string().optional().nullable(),
      })
      .passthrough()
      .optional()
      .nullable(),
    /**
     * `automatic`: se cobra solo con la tarjeta guardada.
     * `manual`: se emite el pendiente y NO se intenta cobrar — es el caso de
     * débito, que MercadoPago no deja cobrar solo sin el CVV.
     */
    collection_mode: z.enum(["automatic", "manual"]).optional().nullable(),
    plan: z
      .object({
        /**
         * De qué plan salió la suscripción (octopus#324, borde ≥ v3.43.0).
         *
         * **`null` significa "no sé"**, no "sin plan": una contratación ADOPTADA
         * no guarda de qué plan vino. Con `null` NO se marca ninguno del
         * catálogo por importe — dos planes pueden valer lo mismo, y marcar el
         * equivocado es justo lo que el dueño lee para decidir.
         */
        plan_id: z.string().optional().nullable(),
        name: z.string().optional().nullable(),
        price: z.string().optional().nullable(),
        currency: z.string().optional().nullable(),
        interval_days: z.number().optional().nullable(),
      })
      .passthrough()
      .optional()
      .nullable(),
    next_charge_at: z.string().optional().nullable(),
    payment_method: z
      .object({
        brand: z.string().optional().nullable(),
        last4: z.string().optional().nullable(),
        type: z.string().optional().nullable(),
      })
      .passthrough()
      .optional()
      .nullable(),
    /** La suma de los pendientes abiertos. String decimal. */
    balance: z
      .object({
        amount: z.string(),
        currency: z.string().optional().nullable(),
      })
      .passthrough()
      .optional()
      .nullable(),
    /** El pendiente abierto más viejo, o `null` si está al día. */
    open_bill: billSchema.optional().nullable(),
    /**
     * En qué escalón de la mora está el negocio y cuándo cae el siguiente
     * (trd-octopus#333, S3). El borde lo pasa verbatim.
     *
     * Son tres escalones y no un booleano: entre "debe" y "no puede operar"
     * hay días, y ese es justo el tiempo en el que avisar sirve de algo.
     *
     * # `step` no es `suspended`
     *
     * `suspended` dice si billing **revocó la capacidad**; `step` dice si el
     * **documento llegó a su corte**. Laudo trd-bff#303: el bloqueo del dueño
     * refleja el claim explícito `suspended` del token, no este read model.
     *
     * # Opcional, sin default
     *
     * Octopus lo manda siempre, pero un octopus anterior a #333 no: ausente es
     * "no sé", y "no sé" no es "al día" ni "cortado". Por eso no tiene
     * default.
     */
    dunning: z
      .object({
        /**
         * `current` (al día) · `overdue` (vencido, todavía opera) · `blocked`
         * (llegó el corte). No es un enum, por lo mismo que `status`: un
         * escalón nuevo no puede vaciar la pantalla.
         */
        step: z.string(),
        /**
         * Cuándo se evaluó. El escalón no es un hecho guardado: sale del
         * vencimiento, de la política y del reloj. Un resumen cacheado puede
         * estar mostrando el escalón de ayer.
         */
        evaluated_at: z.string().optional().nullable(),
        /** Qué sigue. `null` si no hay siguiente (al día, o ya cortado). */
        next_step: z.string().optional().nullable(),
        next_step_at: z.string().optional().nullable(),
        /**
         * Días que faltan, redondeados **hacia arriba**: es el número del aviso,
         * y truncar haría que el último día dijera "en 0 días". `null` si no
         * hay siguiente escalón — **no** es cero.
         */
        days_to_next_step: z.number().optional().nullable(),
        /** La política con la que se emitió el pendiente, no la de hoy. */
        overdue_days: z.number().optional().nullable(),
      })
      .passthrough()
      .optional()
      .nullable(),
  })
  .passthrough();

export type BillingSummary = z.infer<typeof billingSummarySchema>;
export type Mora = NonNullable<BillingSummary["dunning"]>;

export const billsPageSchema = z
  .object({
    items: z.array(billSchema),
    total: z.number().optional().nullable(),
  })
  .passthrough();

export type BillsPage = z.infer<typeof billsPageSchema>;

/** Lo que devuelve `POST /bills/{id}/pay`: a dónde mandar al usuario. */
export const payBillResponseSchema = z
  .object({
    checkout_url: z.string(),
    expires_at: z.string().optional().nullable(),
  })
  .passthrough();

export type PayBillResponse = z.infer<typeof payBillResponseSchema>;

/**
 * Un cambio de plan pedido (trd-client#255).
 *
 * # Se AGENDA, no se aplica
 *
 * `POST /api/v1/business/billing/plan-change` agenda el cambio para el borde del
 * ciclo siguiente: el plan vigente sigue siendo el de hoy, y eso está bien. La
 * pantalla tiene que mostrar el pendiente, no decir "listo, cambiaste".
 *
 * # Y puede rechazarse DESPUÉS de aceptado
 *
 * Octopus revalida al aplicar, porque entre el pedido y el borde del ciclo —30
 * días, o 365— el catálogo o el techo de la org pueden haber cambiado. Por eso
 * el motivo del rechazo vive en el **historial** (`/plan-changes`) y no en el
 * pendiente: para cuando hay rechazo, el pendiente ya no existe y su `GET`
 * contesta **204**.
 *
 * # La cadencia es parte del cambio
 *
 * El mismo plan de mensual a anual **no** es un no-op: por eso viajan las dos
 * cadencias.
 *
 * Nada de enums acá: ver el porqué en `status` y `paid_via` de arriba.
 */
export const planChangeSchema = z
  .object({
    id: z.string(),
    contract_id: z.string().optional().nullable(),
    from_plan_id: z.string().optional().nullable(),
    to_plan_id: z.string(),
    /**
     * Los nombres de los dos planes, resueltos por octopus (octopus#325). El
     * borde los pasa verbatim: sus cuatro handlers de `plan-change` son
     * `forward_as` sobre un `Value` sin tipar.
     *
     * **`null` es "no se sabe cómo se llama", no "no tiene nombre".** Llega
     * `null` cuando el plan ya no está en el catálogo (archivado o
     * despublicado), cuando la contratación fue adoptada y no guarda de qué
     * plan vino, o cuando octopus no pudo leer el catálogo y contesta igual.
     * Son tres causas distintas y desde acá no se distinguen, así que no se
     * inventa un motivo: el id siempre viaja y es lo que identifica.
     */
    from_plan_name: z.string().optional().nullable(),
    to_plan_name: z.string().optional().nullable(),
    from_interval_days: z.number().optional().nullable(),
    to_interval_days: z.number().optional().nullable(),
    /** `pending` · `applied` · `rejected` · `canceled`. */
    status: z.string(),
    requested_by: z.string().optional().nullable(),
    requested_at: z.string().optional().nullable(),
    /** Por qué NO se aplicó. Sólo llega por el historial. */
    reject_reason: z.string().optional().nullable(),
    /** Desde cuándo el borde retiene el cambio, y por qué. */
    held_since: z.string().optional().nullable(),
    hold_reason: z.string().optional().nullable(),
  })
  .passthrough();

export type PlanChange = z.infer<typeof planChangeSchema>;

/** El historial de cambios: la más nueva primero. */
export const planChangesPageSchema = z
  .object({
    items: z.array(planChangeSchema),
    total: z.number().optional().nullable(),
  })
  .passthrough();

/**
 * Un plan del catálogo al que el dueño se puede pasar (trd-bff#285).
 *
 * `GET /api/v1/business/billing/plans` devuelve **sólo los ofrecibles** —el borde
 * los angosta con `status: "active"` Y `is_public: true`, fail-closed— y **sólo
 * los campos comerciales**, por allowlist. La pantalla dibuja lo que le llega:
 * no filtra nada.
 *
 * # La plata es un string decimal
 *
 * `price` viaja como `"25000.00"`. El borde lo mueve verbatim justamente para no
 * pasarlo por un float, y acá vale lo mismo: va por `formatDecimalString`.
 *
 * # `null` en un cupo significa ILIMITADO
 *
 * No cero. Octopus usa la ausencia para "sin tope" y el borde la copia tal cual.
 * Es el mismo malentendido que ya nos costó la pantalla de usuarios, en otra
 * forma: ahí el centinela era `-1`, acá es `null`.
 */
export const planDelCatalogoSchema = z
  .object({
    plan_id: z.string(),
    name: z.string().optional().nullable(),
    price: z.string().optional().nullable(),
    currency: z.string().optional().nullable(),
    interval_days: z.number().optional().nullable(),
    is_free: z.boolean().optional().nullable(),
    /** `null` = sin tope. NO es cero. */
    max_users: z.number().optional().nullable(),
    max_products: z.number().optional().nullable(),
    max_warehouses: z.number().optional().nullable(),
    module_set: z.array(z.string()).optional().nullable(),
    /**
     * Las cadencias **contratables** del plan (trd-bff#289, borde ≥ v3.43.0).
     *
     * **Ausente NO es `[]`.** Contra un octopus viejo el campo no viaja, y el
     * borde **no inventa una lista vacía a propósito**: ausente es *"no sé"*,
     * vacío sería *"este plan no cotiza ninguna cadencia"*. Por eso es
     * `.optional()` y no tiene default — ponerle `[]` acá borraría esa
     * distinción antes de que la pantalla la pueda ver.
     *
     * Hoy octopus todavía no lo manda en dev (v1.33.3): va a aparecer con su
     * release. Hasta entonces se ofrece la cadencia propia del plan, y **no** se
     * dice que no hay anual.
     */
    prices: z
      .array(
        z
          .object({
            interval_days: z.number(),
            /** String decimal, igual que `price`. */
            amount: z.string().optional().nullable(),
            currency: z.string().optional().nullable(),
          })
          .passthrough()
      )
      .optional(),
  })
  .passthrough();

export type PlanDelCatalogo = z.infer<typeof planDelCatalogoSchema>;

/** El catálogo, ya desenvuelto. Ver `desenvolverCatalogo`. */
export const planesDelCatalogoSchema = z.array(planDelCatalogoSchema);
